// components/Users/BadDebtRecoveryForm.jsx
import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { recoverBadDebt, fetchMemberBadDebts } from 'Features/api/api';

const _q2 = (n) => {
  const x = Number(n || 0);
  return Math.round(x * 100) / 100;
};

const BadDebtRecoveryForm = ({ member, onClose, debts }) => {
  const dispatch = useDispatch();
  const reduxDebts = useSelector((s) => s.badDebt?.byMember?.[member?.id]);
  const effectiveDebts = Array.isArray(reduxDebts) ? reduxDebts : Array.isArray(debts) ? debts : [];

  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reduxDebts && member?.id) {
      dispatch(fetchMemberBadDebts({ member_id: member.id }));
    }
  }, [dispatch, member?.id]); // eslint-disable-line

  const totalOutstanding = useMemo(() => {
    return _q2((effectiveDebts || []).reduce((a, d) => a + Number(d?.outstanding || 0), 0));
  }, [effectiveDebts]);

  const payAll = () => setAmount(String(totalOutstanding || ''));

  const preview = useMemo(() => {
    const amt = _q2(amount);
    const principal = _q2((amt * 100) / 110);
    const interest = _q2(amt - principal);
    return { amt, principal, interest };
  }, [amount]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const amt = _q2(amount);
    if (amt <= 0) return;

    setLoading(true);
    try {
      await dispatch(
        recoverBadDebt({
          member_id: member.id,
          recovered_amount: amt,
        })
      ).unwrap();

      await dispatch(fetchMemberBadDebts({ member_id: member.id }));
      setAmount('');
    } catch (err) {
      // handled via toast in thunk
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(Number(n || 0));

  return (
    <Wrap>
      <h3>
        Bad Debt Recovery — {member?.first_name} {member?.last_name}
      </h3>

      <Section>
        <Row>
          <Label>Total Outstanding</Label>
          <Strong>{fmt(totalOutstanding)}</Strong>
        </Row>

        {effectiveDebts?.length > 0 ? (
          <DebtList>
            {effectiveDebts.map((d) => (
              <DebtRow key={d.id}>
                <div>
                  <DebtTitle>Bad Debt #{d.id}</DebtTitle>
                  <DebtMeta>
                    Created: {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                  </DebtMeta>
                  <DebtMeta>Status: {d.status}</DebtMeta>
                </div>
                <DebtAmounts>
                  <SmallRow>
                    <span>Principal</span>
                    <b>{fmt(d.principal)}</b>
                  </SmallRow>
                  <SmallRow>
                    <span>Interest</span>
                    <b>{fmt(d.interest_receivable)}</b>
                  </SmallRow>
                  <SmallRow>
                    <span>Outstanding</span>
                    <b>{fmt(d.outstanding)}</b>
                  </SmallRow>
                </DebtAmounts>
              </DebtRow>
            ))}
          </DebtList>
        ) : (
          <Empty>There are no open bad debts for this member.</Empty>
        )}
      </Section>

      <Form onSubmit={onSubmit}>
        <Label>Amount Paid</Label>
        <Input
          type="number"
          step="0.01"
          value={amount}
          placeholder="0.00"
          onChange={(e) => setAmount(e.target.value)}
        />

        <QuickRow>
          <Quick onClick={payAll} type="button">
            Pay Full Outstanding
          </Quick>
          <Quick type="button" onClick={() => setAmount('')}>
            Clear
          </Quick>
        </QuickRow>

        <PreviewBox>
          <PreviewRow>
            <span>Will apply to Principal</span>
            <b>{fmt(preview.principal)}</b>
          </PreviewRow>
          <PreviewRow>
            <span>Will apply to Interest</span>
            <b>{fmt(preview.interest)}</b>
          </PreviewRow>
          <PreviewRow>
            <span>Total This Payment</span>
            <Total>{fmt(preview.amt)}</Total>
          </PreviewRow>
        </PreviewBox>

        <Actions>
          <Btn type="submit" $variant="primary" disabled={preview.amt <= 0 || loading}>
            {loading ? (
              <>
                <Spinner /> Processing Payment…
              </>
            ) : (
              'Record Recovery'
            )}
          </Btn>
          <Btn type="button" onClick={onClose}>
            Close
          </Btn>
        </Actions>

        <Footnote>
          Split preview uses your backend rule: <code>principal = payment × 100/110</code>,{' '}
          <code>interest = payment − principal</code>.
        </Footnote>
      </Form>
    </Wrap>
  );
};

export default BadDebtRecoveryForm;

/* =============== styled-components =============== */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 520px;
  max-width: 680px;
`;

const Section = styled.div`
  background: #f9fafb;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 12px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #667085;
`;

const Strong = styled.div`
  font-weight: 700;
  color: #101828;
`;

const DebtList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DebtRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 8px;
  border: 1px dashed #e4e7ec;
  border-radius: 8px;
  padding: 8px;
`;

const DebtTitle = styled.div`
  font-weight: 700;
  color: #101828;
`;

const DebtMeta = styled.div`
  font-size: 12px;
  color: #667085;
`;

const DebtAmounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
`;

const SmallRow = styled.div`
  display: flex;
  gap: 10px;
  font-size: 12px;
`;

const Empty = styled.div`
  font-size: 12px;
  color: #667085;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #175cd3;
    box-shadow: 0 0 0 3px rgba(23, 92, 211, 0.12);
  }
`;

const QuickRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Quick = styled.button`
  padding: 6px 10px;
  border-radius: 8px;
  background: #f2f4f7;
  border: 1px solid #e4e7ec;
  cursor: pointer;
`;

const PreviewBox = styled.div`
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 10px;
  background: #fff;
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  & + & {
    margin-top: 6px;
  }
`;

const Total = styled.b`
  font-size: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Btn = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${({ $variant }) => ($variant === 'primary' ? '#0F766E' : '#fff')};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#344054')};
  box-shadow: ${({ $variant }) =>
    $variant === 'primary'
      ? '0 1px 2px rgba(16,24,40,0.06)'
      : 'inset 0 0 0 1px #D0D5DD'};
`;

const Spinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Footnote = styled.div`
  font-size: 11px;
  color: #98a2b3;
`;
